import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { IconChevronDown, IconSearch, IconX, IconCheck, IconUser } from '@tabler/icons-react';
import { useTenantUsers, userDisplayName } from '../../../hooks/useTenantUsers';
import { TenantUserDto } from '../../../api/model';
import styles from './UserPicker.module.scss';

// singleselect
interface UserPickerSingleProps {
  multi?: false;
  value?: number | null;
  onChange: (userId: number | undefined) => void;
  placeholder?: string;
  disabled?: boolean;
  allowClear?: boolean;
  users?: TenantUserDto[];
}

// multi-select
interface UserPickerMultiProps {
  multi: true;
  value?: number[] | null;
  onChange: (userIds: number[]) => void;
  placeholder?: string;
  disabled?: boolean;
  allowClear?: boolean;
  users?: TenantUserDto[];
}

type UserPickerProps = UserPickerSingleProps | UserPickerMultiProps;

// build avatar chip
function initials(user: TenantUserDto): string {
  const a = (user.firstName?.[0] || '').toUpperCase();
  const b = (user.lastName?.[0] || '').toUpperCase();
  const combined = `${a}${b}`.trim();
  if (combined) return combined;
  return (user.username?.[0] || '?').toUpperCase();
}

const UserPicker: React.FC<UserPickerProps> = (props) => {
  const { placeholder, disabled, allowClear = true, users: usersProp } = props;
  const { t } = useTranslation('settings');
  const { users: allUsers, isLoading } = useTenantUsers();
  const users = usersProp ?? allUsers;
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const rootRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  const isMulti = props.multi === true;
  const selectedIds: number[] = isMulti
    ? ((props as UserPickerMultiProps).value ?? [])
    : ((props as UserPickerSingleProps).value != null ? [(props as UserPickerSingleProps).value!] : []);

  const selectedUsers = useMemo(
    () => selectedIds.map(id => users.find(u => u.id === id)).filter((u): u is TenantUserDto => !!u),
    [users, selectedIds]
  );

  // close on outside click
  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      // claimed in the capture phase, so the dialog around the picker sees it
      // as handled and stays open
      if (e.key === 'Escape') {
        e.preventDefault();
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey, true);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey, true);
    };
  }, [open]);

  useEffect(() => {
    if (open) {
      setQuery('');
      const id = window.setTimeout(() => searchRef.current?.focus(), 30);
      return () => window.clearTimeout(id);
    }
  }, [open]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return users;
    return users.filter(u => {
      const hay = `${userDisplayName(u)} ${u.username ?? ''} ${u.tenantRoleDisplayName ?? ''}`.toLowerCase();
      return hay.includes(q);
    });
  }, [users, query]);

  const handleSelect = (id?: number) => {
    if (isMulti) {
      const multiProps = props as UserPickerMultiProps;
      if (id == null) {
        multiProps.onChange([]);
      } else {
        const current = multiProps.value ?? [];
        const next = current.includes(id) ? current.filter(x => x !== id) : [...current, id];
        multiProps.onChange(next);
      }
      // stay open for multi-select
    } else {
      (props as UserPickerSingleProps).onChange(id);
      setOpen(false);
    }
  };

  return (
    <div className={styles.picker} ref={rootRef}>
      <button
        type="button"
        className={`${styles.trigger} ${open ? styles.triggerOpen : ''}`}
        onClick={() => !disabled && setOpen(o => !o)}
        disabled={disabled}
      >
        {selectedUsers.length > 0 ? (
          isMulti ? (
            <span className={styles.multiChips}>
              {selectedUsers.map(u => (
                <span key={u.id} className={styles.chip}>
                  <span className={styles.chipAvatar}>{initials(u)}</span>
                  <span className={styles.chipName}>{userDisplayName(u)}</span>
                  <span
                    className={styles.chipRemove}
                    role="button"
                    onMouseDown={e => {
                      e.stopPropagation();
                      handleSelect(u.id);
                    }}
                  >
                    <IconX size={11} />
                  </span>
                </span>
              ))}
            </span>
          ) : (
            <span className={styles.selectedWrap}>
              <span className={styles.avatar}>{initials(selectedUsers[0])}</span>
              <span className={styles.selectedName}>{userDisplayName(selectedUsers[0])}</span>
              {selectedUsers[0].tenantRoleDisplayName && (
                <span className={styles.roleBadge}>{selectedUsers[0].tenantRoleDisplayName}</span>
              )}
            </span>
          )
        ) : (
          <span className={styles.placeholder}>
            <IconUser size={16} className={styles.placeholderIcon} />
            {placeholder ?? t('user.picker.placeholder', 'Select a user…')}
          </span>
        )}
        <IconChevronDown size={16} className={`${styles.chevron} ${open ? styles.chevronOpen : ''}`} />
      </button>

      {open && (
        <div className={styles.popover}>
          <div className={styles.searchRow}>
            <IconSearch size={15} className={styles.searchIcon} />
            <input
              ref={searchRef}
              type="text"
              className={styles.searchInput}
              value={query}
              onChange={e => setQuery(e.target.value)}
              placeholder={t('user.picker.search', 'Search people…')}
            />
          </div>

          <div className={styles.list}>
            {allowClear && (
              <button
                type="button"
                className={`${styles.option} ${selectedIds.length === 0 ? styles.optionActive : ''}`}
                onClick={() => handleSelect(undefined)}
              >
                <span className={styles.unassignedIcon}><IconX size={14} /></span>
                <span className={styles.optionMain}>
                  <span className={styles.optionName}>{t('user.picker.unassigned', 'Unassigned')}</span>
                </span>
                {selectedIds.length === 0 && <IconCheck size={15} className={styles.checkIcon} />}
              </button>
            )}

            {isLoading && (
              <div className={styles.hint}>{t('user.picker.loading', 'Loading people…')}</div>
            )}

            {!isLoading && filtered.length === 0 && (
              <div className={styles.hint}>{t('user.picker.empty', 'No people match your search.')}</div>
            )}

            {filtered.map(u => {
              const isSelected = selectedIds.includes(u.id!);
              return (
                <button
                  key={u.id}
                  type="button"
                  className={`${styles.option} ${isSelected ? styles.optionActive : ''}`}
                  onClick={() => handleSelect(u.id)}
                >
                  <span className={styles.avatar}>{initials(u)}</span>
                  <span className={styles.optionMain}>
                    <span className={styles.optionName}>{userDisplayName(u)}</span>
                    <span className={styles.optionMeta}>
                      @{u.username}
                      {u.tenantRoleDisplayName ? ` · ${u.tenantRoleDisplayName}` : ''}
                    </span>
                  </span>
                  {isSelected && <IconCheck size={15} className={styles.checkIcon} />}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default UserPicker;
